import { Box, Chip, styled, Typography } from "@mui/material";
import { formatCurrency, productDetailsPageUrl } from "common";
import BazarCard from "components/BazarCard";
import LazyImage from "components/LazyImage";
import { H3, Tiny } from "components/Typography";
import { FlexBox } from "../flex-box";
import React, { useMemo, useState } from "react";
import useResolution from "hooks/useResolution";
import { ProductWithPromo } from "modules/promotional/types";
import { PromotionalContentModal } from "./PromotionalContentModal";
import { useRouter } from "next/router";
import usePromotionalContentModal from "hooks/promotional/usePromotionalContentModal";
import { PromoBadges } from "components/product-cards/PromoBadges";

const StyledBazarCard = styled(BazarCard)(() => ({
  height: "100%",
  margin: "auto",
  display: "flex",
  overflow: "hidden",
  borderRadius: "8px",
  position: "relative",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "all 250ms ease-in-out",
}));

type Props = {
  data: ProductWithPromo;
};

const PromotionalCard2: React.FC<Props> = ({ data }) => {
  const { push } = useRouter();
  const { isMobile } = useResolution();
  const { showContent, setShowContent, promotionalID, openContent } =
    usePromotionalContentModal();

  const handleNavigate = () => {
    push(productDetailsPageUrl(data.slug, data.id));
  };

  const savedAmount = useMemo(() => {
    const str = JSON.stringify(data.discounted_amount).split("");
    if (str.length > 3) {
      str.splice(str.length - 3, 0, ",");
    }

    return str.join("");
  }, [data]);

  return (
    <StyledBazarCard
      sx={{
        height: "100%",
        display: "flex",
        borderRadius: "8px",
        flexDirection: "column",
      }}
    >
      <PromotionalContentModal
        uuid={promotionalID}
        open={showContent}
        onClose={() => setShowContent(false)}
      />
      <a onClick={handleNavigate}>
        <Box
          sx={{
            marginTop: "16px",
            paddingX: "16px",
            alignItems: "center",
          }}
        >
          <FlexBox
            sx={{
              justifyContent: "end",
              mb: "-2em",
            }}
          >
            <Chip
              label={`SAVE ₱${savedAmount}`}
              sx={{
                fontSize: ".8em",
                bgcolor: "#2B2EA2",
                color: "white",
                height: "1.6em",
                zIndex: "3",
              }}
            />
          </FlexBox>
          <LazyImage
            src={data?.img_thumbnail || "/assets/images/kachi/kachi-crying.png"}
            width={0}
            height={0}
            layout="responsive"
            objectFit="contain"
            alt={data.product_name}
          />
        </Box>
      </a>

      <Box sx={{ padding: "1rem" }}>
        <a onClick={handleNavigate}>
          <FlexBox>
            <Box flex="1 1 0" minWidth="0px" mr={1}>
              <H3
                mb={1}
                title={data?.product_name}
                fontSize="14px"
                fontWeight="600"
                className="title"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  overflow: "hidden",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                }}
              >
                {data?.product_name}
              </H3>

              <FlexBox
                sx={{
                  alignItems: isMobile ? "start" : "center",
                  flexDirection: isMobile ? "column" : "flex",
                }}
              >
                <Typography
                  color={"primary"}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.2em",
                  }}
                >
                  {formatCurrency(data?.amount)}
                </Typography>
                <Tiny
                  sx={{
                    marginLeft: !isMobile ? ".5em" : "0px",
                    height: "100%",
                    textDecoration: "line-through",
                    color: "#7D879C",
                    fontSize: "1em",
                  }}
                >
                  {formatCurrency(data?.standard_amount)}
                </Tiny>
              </FlexBox>
            </Box>
          </FlexBox>
        </a>

        {/* todo: if badges are more than 3 display latest 3 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <PromoBadges
            promotionalBadge={data?.promotional_badges || []}
            onClick={(id?: string) => openContent(id)}
          />
        </Box>
      </Box>
    </StyledBazarCard>
  );
};

export default PromotionalCard2;
